import {
  CreateMetaAdSetPayload,
  CreateMetaCampaignPayload,
  META_DEFAULT_AGE_MIN,
  META_MAX_AGE_MIN,
  MetaSpecialAdCategory,
  MetaTrafficPerformanceGoal,
  stripMetaAdAccountPrefix
} from 'shared/src/meta/meta-types';
import type {
  AdapterCapabilities,
  AdapterConfig,
  AdapterSchemas,
  AncestryMap
} from '../skill-platform-types';

// The media buy is the campaign, not the ad set created beneath it — Snowflake keys Meta buys at
// the campaign (docs/adr/0004-meta-media-buy-is-the-campaign.md).
export const metaAdapterConfig: AdapterConfig = {
  platformName: 'Meta',
  platformShortLabel: 'META',
  fullHierarchy: [
    { key: 'account', label: 'Ad Account' },
    { key: 'campaign', label: 'Campaign' },
    { key: 'ad_set', label: 'Ad Set' }
  ],
  createSequence: ['campaign', 'ad_set'],
  requiredAncestors: ['account'],
  rootEntityType: 'account',
  mediaBuyEntityType: 'campaign',
  workflowSteps: ['Configure', 'Review']
};

const ADS_MANAGER_BASE_URL = 'https://adsmanager.facebook.com/adsmanager/manage';

export function buildMetaEntityUrl(
  entityType: string,
  entityId: string,
  ancestry?: AncestryMap
): string {
  const accountId = ancestry?.get('account')?.platformEntityId;
  const accountQs = accountId ? `?act=${stripMetaAdAccountPrefix(accountId)}` : '';
  const sep = accountQs ? '&' : '?';
  if (entityType === 'campaign') {
    return `${ADS_MANAGER_BASE_URL}/campaigns${accountQs}${sep}selected_campaign_ids=${entityId}`;
  }
  if (entityType === 'ad_set') {
    return `${ADS_MANAGER_BASE_URL}/adsets${accountQs}${sep}selected_adset_ids=${entityId}`;
  }
  if (entityType === 'account') {
    return `${ADS_MANAGER_BASE_URL}/campaigns?act=${stripMetaAdAccountPrefix(entityId)}`;
  }
  return 'https://adsmanager.facebook.com/';
}

export const metaAdapterSchemas: AdapterSchemas = {
  entityPayloads: {
    campaign: CreateMetaCampaignPayload,
    ad_set: CreateMetaAdSetPayload
  }
};

export const metaCapabilities: AdapterCapabilities = {
  // The Objectives (CONTEXT.md): a Meta buy normally runs as one campaign of each.
  lineItemTypes: ['awareness', 'traffic'],
  lineItemTypeAliases: {
    awareness: ['reach', 'brand awareness', 'impressions'],
    traffic: ['link clicks', 'landing page views', 'clicks']
  },
  requiresAccountId: true,
  accountLabel: 'ad account',
  accountIdPlaceholder: 'e.g. act_1234567890',
  biddingStrategies: ['LOWEST_COST_WITHOUT_CAP'],
  defaultBiddingStrategy: 'LOWEST_COST_WITHOUT_CAP',
  // No pacingOptions: Meta paces a lifetime budget itself; the card hides its pacing dropdown.
  budgetTypes: ['lifetime'],
  // No usesCpmBid: Meta has no bids, only budget.
  // Default split is Awareness 60 / Traffic 40, each campaign its own media plan.
  supportsCampaignSplit: true,
  objectiveSplit: {
    defaults: [
      { objective: 'awareness', sharePercent: 60 },
      { objective: 'traffic', sharePercent: 40 }
    ],
    performanceGoals: { traffic: MetaTrafficPerformanceGoal.options.map(o => o.value) }
  },
  requiresPageId: true,
  audience: {
    specialAdCategories: MetaSpecialAdCategory.options.map(o => o.value),
    defaultAgeMin: META_DEFAULT_AGE_MIN,
    maxAgeMin: META_MAX_AGE_MIN
  },
  targetingModules: [
    {
      key: 'geo.location',
      label: 'Geo Target',
      kind: 'multi-select',
      optionsSource: 'zoho-derived',
      zohoField: 'Geo_Targets'
    }
  ]
};
